import type { KeyboardEventHandler } from 'react';
import React from 'react';

import Icons from '@/core/components/Icons';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import isNullish from '@/core/lib/utils/isNullish';

export const InnerInput: React.FC<{
  isFocused: boolean;
  isError: boolean;
  isCleanable: boolean;
  isSuccess: boolean;
  isDisabled: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  type: 'text' | 'email' | undefined;
  onKeyDown: KeyboardEventHandler | undefined;
  autoComplete: 'email' | 'name' | 'username' | 'address-level2' | 'off' | undefined;
  inputMode: 'text' | 'email' | 'none' | 'tel' | 'url' | 'numeric' | 'decimal' | 'search' | undefined;
  enterKeyHint: 'search' | 'enter' | 'done' | 'go' | 'next' | 'previous' | 'send' | undefined;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  onClick?: ((event: React.MouseEvent<HTMLInputElement, MouseEvent>) => void) | undefined;
  id: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  cleanAttrs: {
    onMouseDown: (event: React.MouseEvent<Element, MouseEvent>) => void;
    onClick: () => void;
    onBlur: () => void;
    onFocus: () => void;
  };
  noCheck?: boolean | undefined;
}> = ({ isFocused, isError, isCleanable, isSuccess, isDisabled, value, inputRef, cleanAttrs, noCheck, ...props }) => {
  const { t } = useTranslationContext(['common']);

  const isFocusedClasses = isFocused ? 'bg-bg-primary ring-2 ring-stroke-default-focus/20 border-stroke-default-focus' : '';
  const isErrorClasses = isError ? '!border-stroke-danger-focus ring-2 !ring-stroke-danger-focus/20' : '';
  const hasRightIconClasses = isCleanable || isSuccess ? 'pr-8' : 'pr-3';
  const isDisabledClasses = isDisabled ? 'border-transparent bg-bg-pale' : '';

  const hasValue = !isNullish(value) ? 'bg-bg-primary border-content-placeholder' : '';

  return (
    <>
      <input
        ref={inputRef}
        className={`text-body-secondary placeholder:text-body-secondary z-0 w-full rounded-2 border border-bg-secondary bg-bg-primary py-2 pl-3 font-normal text-content-primary outline-none placeholder:font-normal placeholder:text-content-placeholder ${isFocusedClasses} ${isErrorClasses} ${hasRightIconClasses} ${isDisabledClasses} ${hasValue}`}
        value={value}
        {...props}
      />
      {isCleanable && (
        <button type="button" className="absolute inset-y-0 right-0 z-10 my-auto mr-3 h-5" aria-label={t('inputs.text.clean', { ns: 'common' })} {...cleanAttrs}>
          <Icons icon="close" size="20" color="placeholder" />
        </button>
      )}

      {isSuccess && !noCheck && (
        <div className="absolute inset-y-0 right-0 my-auto mr-3 h-5">
          <Icons icon="check" size="20" color="success" />
        </div>
      )}
    </>
  );
};
